/** Grand Quack Auto — city generator (6×6 block grid). See research.md §2-3. */

import * as THREE from 'three';
import {
  BUILDING_HEIGHT, CITY_BLOCK_GRID, CITY_BLOCK_SIZE,
  GROUND_SIZE, PALETTE, ROAD_WIDTH,
} from '../config';
import type { World } from '../types';
import { mulberry32 } from '../utils/mathUtils';

const STRIDE = CITY_BLOCK_SIZE + ROAD_WIDTH; // 16
const GRID = CITY_BLOCK_GRID; // 6
const BUILDING_COLORS = [
  PALETTE.buildingA, PALETTE.buildingB, PALETTE.buildingC,
  PALETTE.buildingD, PALETTE.buildingE,
];

export const sidewalkPaths: THREE.Vector3[][] = [];
export const parkPositions: THREE.Vector3[] = [];
export let CAR_SPAWNS: (readonly [number, number, number])[] = [];

const blockCenter = (i: number): number => (i - (GRID - 1) / 2) * STRIDE;
const roadCenter = (i: number): number => (i - GRID / 2) * STRIDE;

export function generateCity(world: World): void {
  generateCarSpawns();
  generateGround(world);
  generateRoads(world);
  generateBuildings(world);
  generateWaypoints();
}

/** Pick 3 random inner road intersections (not at origin where duck spawns). */
function generateCarSpawns(): void {
  const pts: (readonly [number, number, number])[] = [];
  for (let i = 1; i < GRID; i++)
    for (let j = 1; j < GRID; j++) {
      const x = roadCenter(i), z = roadCenter(j);
      if (x === 0 && z === 0) continue;
      pts.push([x, 1.5, z] as const);
    }
  for (let i = pts.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pts[i], pts[j]] = [pts[j]!, pts[i]!];
  }
  CAR_SPAWNS = pts.slice(0, 3);
}

function generateGround(world: World): void {
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(GROUND_SIZE, GROUND_SIZE),
    new THREE.MeshToonMaterial({ color: PALETTE.ground }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  ground.name = 'ground';
  world.scene.add(ground);
}

// ── Roads ─────────────────────────────────────────────────────────────────

function generateRoads(world: World): void {
  // 7 horizontal + 7 vertical road strips = 14 instances, 1 draw call.
  const geom = new THREE.PlaneGeometry(GROUND_SIZE, ROAD_WIDTH);
  const mat = new THREE.MeshToonMaterial({ color: PALETTE.road });
  const roads = new THREE.InstancedMesh(geom, mat, 14);
  roads.receiveShadow = true;
  roads.name = 'roads';

  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const qH = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
  const qV = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, Math.PI / 2, 0));
  const one = new THREE.Vector3(1, 1, 1);
  for (let i = 0; i <= GRID; i++) {
    const rc = roadCenter(i);
    pos.set(0, 0.01, rc); m.compose(pos, qH, one); roads.setMatrixAt(i, m);
    pos.set(rc, 0.01, 0); m.compose(pos, qV, one); roads.setMatrixAt(GRID + 1 + i, m);
  }
  roads.instanceMatrix.needsUpdate = true;
  world.scene.add(roads);
}

export interface BuildingDef { x: number; z: number; w: number; h: number; d: number; color: number }
/** All building AABBs — populated by generateCity, read by buildingCollision. */
export const buildingDefs: BuildingDef[] = [];

/** True if (x,z) is within clearance of any car spawn (keeps roads driveable). */
function nearCarSpawn(x: number, z: number, clearance: number): boolean {
  for (const [sx, , sz] of CAR_SPAWNS) {
    if (Math.abs(x - sx) < clearance && Math.abs(z - sz) < clearance) return true;
  }
  return false;
}

function generateBuildings(world: World): void {
  // Random seed per session — every player gets a unique city.
  const rng = mulberry32(Date.now() ^ (Math.random() * 0x7fffffff));
  const buildings: BuildingDef[] = [];
  const sub = CITY_BLOCK_SIZE / 2; // 6 — sub-cell size (2×2 per block)
  const maxDist = Math.hypot(blockCenter(GRID - 1), blockCenter(GRID - 1));

  for (let row = 0; row < GRID; row++) {
    for (let col = 0; col < GRID; col++) {
      const cx = blockCenter(col);
      const cz = blockCenter(row);

      // ~15% of blocks are parks (empty green space).
      if (rng() < 0.15) {
        parkPositions.push(new THREE.Vector3(cx, 0, cz));
        continue;
      }

      // Height bias: taller buildings near city center.
      const dist = Math.hypot(cx, cz) / maxDist;
      const heightBias = 1 - dist * 0.5;

      // 2×2 sub-grid inset from block edges so buildings can't reach roads.
      const cellSpan = sub - 1; // 5 — leaves ≥1 unit margin to road edge
      for (let sr = 0; sr < 2; sr++) {
        for (let sc = 0; sc < 2; sc++) {
          if (rng() < 0.25) continue; // 25% empty for gaps

          const bx = cx + (sc - 0.5) * cellSpan;
          const bz = cz + (sr - 0.5) * cellSpan;
          const w = cellSpan; // fills sub-cell exactly — zero gap, no dark seams
          const d = cellSpan;
          const rawH = BUILDING_HEIGHT.min + rng() * (BUILDING_HEIGHT.max - BUILDING_HEIGHT.min);
          const h = Math.max(BUILDING_HEIGHT.min, rawH * heightBias);

          // Skip buildings that encroach on car spawn intersections.
          if (nearCarSpawn(bx, bz, w / 2 + 4)) continue;

          buildings.push({
            x: bx, z: bz, w, h, d,
            color: BUILDING_COLORS[Math.floor(rng() * BUILDING_COLORS.length)]!,
          });
        }
      }
    }
  }

  const valid = buildings.filter(b => b.w >= 4 && b.d >= 4);

  // Expose building AABBs for collision checks (ducklings, safety net).
  buildingDefs.length = 0;
  buildingDefs.push(...valid);

  // Single InstancedMesh for all buildings — 1 draw call (research §2).
  const geom = new THREE.BoxGeometry(1, 1, 1);
  const mat = new THREE.MeshToonMaterial();
  const mesh = new THREE.InstancedMesh(geom, mat, valid.length);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.name = 'buildings';

  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const q = new THREE.Quaternion();
  const scl = new THREE.Vector3();
  const c = new THREE.Color();

  for (let i = 0; i < valid.length; i++) {
    const b = valid[i]!;
    pos.set(b.x, b.h / 2, b.z); // bottom face at y=0
    scl.set(b.w, b.h, b.d);
    m.compose(pos, q, scl);
    mesh.setMatrixAt(i, m);
    c.setHex(b.color);
    mesh.setColorAt(i, c);
  }
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  world.scene.add(mesh);
}

// ── Waypoints ─────────────────────────────────────────────────────────────

function generateWaypoints(): void {
  sidewalkPaths.length = 0;
  const half = CITY_BLOCK_SIZE / 2 - 0.5; // slightly inside block edge

  for (let row = 0; row < GRID; row++) {
    for (let col = 0; col < GRID; col++) {
      const cx = blockCenter(col);
      const cz = blockCenter(row);
      // Rectangular loop: 8 waypoints (corners + midpoints).
      sidewalkPaths.push([
        new THREE.Vector3(cx - half, 0, cz - half),
        new THREE.Vector3(cx, 0, cz - half),
        new THREE.Vector3(cx + half, 0, cz - half),
        new THREE.Vector3(cx + half, 0, cz),
        new THREE.Vector3(cx + half, 0, cz + half),
        new THREE.Vector3(cx, 0, cz + half),
        new THREE.Vector3(cx - half, 0, cz + half),
        new THREE.Vector3(cx - half, 0, cz),
      ]);
    }
  }
}
